// AthleticInfoStep Component
// Screen 3: Collect athletic/sports information (REQUIRED)

import React, { useMemo } from 'react';
import { FaRunning } from 'react-icons/fa';
import { useSports } from '../../../hooks/useSports';
import styles from './AthleticInfoStep.module.css';

// Fallback positions when API positions are unavailable
const FALLBACK_POSITIONS = {
  'Soccer': ['Forward', 'Midfielder', 'Defender', 'Goalkeeper'],
  'Basketball': ['Point Guard', 'Shooting Guard', 'Small Forward', 'Power Forward', 'Center'],
  'Baseball': ['Pitcher', 'Catcher', 'Infielder', 'Outfielder'],
  'Softball': ['Pitcher', 'Catcher', 'Infielder', 'Outfielder'],
  'Football': ['Quarterback', 'Running Back', 'Wide Receiver', 'Tight End', 'Offensive Line', 'Defensive Line', 'Linebacker', 'Defensive Back', 'Special Teams'],
  'Volleyball': ['Outside Hitter', 'Opposite Hitter', 'Middle Blocker', 'Setter', 'Libero'],
  'Hockey': ['Goalie', 'Defenseman', 'Forward', 'Center', 'Wing'],
  'Lacrosse': ['Attack', 'Midfield', 'Defense', 'Goalie'],
};

const AthleticInfoStep = ({ formData, updateFormData, onNext, onBack, isOptional = false, onSkip }) => {
  const { sportNames, getPositions, getEmoji, loading: sportsLoading } = useSports();

  const levels = [
    { value: 'youth', label: 'Youth (Under 12)' },
    { value: 'junior', label: 'Junior (12-15)' },
    { value: 'high_school', label: 'High School' },
    { value: 'college', label: 'College/University' },
    { value: 'semi_pro', label: 'Semi-Professional' },
    { value: 'professional', label: 'Professional' }
  ];

  const experienceYears = [
    { value: '0-1', label: 'Less than 1 year' },
    { value: '1-3', label: '1-3 years' },
    { value: '3-5', label: '3-5 years' },
    { value: '5-10', label: '5-10 years' },
    { value: '10+', label: '10+ years' }
  ];

  const handleChange = (field, value) => {
    updateFormData({ [field]: value });
  };

  const handlePositionToggle = (position) => {
    const positions = formData.positions || [];
    if (positions.includes(position)) {
      updateFormData({ positions: positions.filter(p => p !== position) });
    } else {
      updateFormData({ positions: [...positions, position] });
    }
  };

  const getPositionsForSport = (sport) => {
    const apiPositions = getPositions(sport);
    return apiPositions.length > 0 ? apiPositions : (FALLBACK_POSITIONS[sport] || ['Player']);
  };

  const positions = useMemo(() => getPositionsForSport(formData.primarySport), [formData.primarySport, getPositions]);

  const isFormValid = () => {
    if (isOptional) return true;
    return (
      formData.primarySport !== '' &&
      formData.positions.length > 0 &&
      formData.currentLevel !== ''
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Athletic Information</h2>
        <p className={styles.subtitle}>
          {isOptional ? 'Optional — you can skip or fill in later' : 'Tell us about your sport'}
        </p>
      </div>

      <div className={styles.content}>
        {/* Primary Sport */}
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Primary Sport {!isOptional && <span className={styles.required}>*</span>}
          </label>
          <select
            className={styles.select}
            value={formData.primarySport}
            onChange={(e) => {
              handleChange('primarySport', e.target.value);
              updateFormData({ positions: [] });
            }}
            disabled={sportsLoading}
          >
            <option value="">
              {sportsLoading ? 'Loading sports...' : 'Select a sport...'}
            </option>
            {sportNames.map((sport) => (
              <option key={sport} value={sport}>{getEmoji(sport)} {sport}</option>
            ))}
          </select>
        </div>

        {/* Positions */}
        {formData.primarySport && (
          <div className={styles.formGroup}>
            <label className={styles.label}>
              Position(s) {!isOptional && <span className={styles.required}>*</span>}
            </label>
            <div className={styles.positionsGrid}>
              {positions.map((position) => (
                <button
                  key={position}
                  type="button"
                  className={`${styles.positionChip} ${
                    formData.positions.includes(position) ? styles.selected : ''
                  }`}
                  onClick={() => handlePositionToggle(position)}
                >
                  {position}
                  {formData.positions.includes(position) && ' ✓'}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Years of Experience */}
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Years of Experience
          </label>
          <select
            className={styles.select}
            value={formData.yearsExperience}
            onChange={(e) => handleChange('yearsExperience', e.target.value)}
          >
            <option value="">Select experience...</option>
            {experienceYears.map((exp) => (
              <option key={exp.value} value={exp.value}>{exp.label}</option>
            ))}
          </select>
        </div>

        {/* Current Level */}
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Current Level {!isOptional && <span className={styles.required}>*</span>}
          </label>
          <select
            className={styles.select}
            value={formData.currentLevel}
            onChange={(e) => handleChange('currentLevel', e.target.value)}
          >
            <option value="">Select level...</option>
            {levels.map((level) => (
              <option key={level.value} value={level.value}>{level.label}</option>
            ))}
          </select>
        </div>

        {/* Buttons */}
        <div className={styles.buttons}>
          <button
            className={styles.backButton}
            onClick={onBack}
          >
            Back
          </button>
          {isOptional && (
            <button
              className={styles.skipButton}
              onClick={onSkip}
            >
              Skip
            </button>
          )}
          <button
            className={styles.nextButton}
            onClick={onNext}
            disabled={!isFormValid()}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default AthleticInfoStep;

